import { useMemo } from 'react';
import { usePayment } from '$api-clients/hooks';
import {
  buildReportingSummaryPerformanceQuery,
  getPercentage,
} from '../../helpers';
import { useAllTimeStartDate } from '../../ReportsConfiguration/useReportsConfiguration';
import type { ReportingSummaryResult } from '../../types';
import { usePerformanceReportContext } from '../context/PerformanceReportContext';
import { PerformanceReportFilterTransactionType } from '../types';

export type GeographyGroupBy = 'CITY' | 'ZIP_CODE';

/** View model row for the Geography Performance grid */
export interface GeographyPerformanceRow {
  id: string;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  totalTransactions: number | null;
  salesVolume: number | null;
  averageSaleValue: number | null;
  adjustedGci: number | null;
  totalSalesVolume: number | null;
  salesVolumePercent: number;
  averageCommissionAmount: number | null;
}

function mapApiRowToGeographyPerformanceRow({
  row,
  index,
  totalSalesVolume,
  groupByValue,
}: {
  row: ReportingSummaryResult;
  index: number;
  totalSalesVolume: number;
  groupByValue: GeographyGroupBy;
}): GeographyPerformanceRow {
  const salesVolume = row.salesVolume;
  const sides = row.sides;
  const averageSaleValue = row.averageSalesValue;
  const adjustedGci = row.adjustedGrossCommission;
  const city = row.city ?? null;
  const state = row.state ?? null;
  const zipCode = row.zipCode ?? null;

  let id: string;
  if (groupByValue === 'ZIP_CODE') {
    id =
      [zipCode, city].filter(Boolean).join(', ') ||
      `row-${index}-${row.sides}-${row.salesVolume}`;
  } else {
    id =
      [city, state].filter(Boolean).join(', ') ||
      `row-${index}-${row.sides}-${row.salesVolume}`;
  }

  return {
    id,
    city,
    state,
    zipCode,
    totalTransactions: sides,
    salesVolume,
    averageSaleValue,
    adjustedGci,
    totalSalesVolume,
    salesVolumePercent: getPercentage(salesVolume, totalSalesVolume),
    averageCommissionAmount: row.averageCommissionAmount,
  };
}
/**
 * Fetches geography performance from GET /reporting/summary with groupBy: CITY or ZIP_CODE,
 * using Performance report filters from context. Returns rows with % of total sales volume.
 */
export function useGeographyPerformance(
  viewReportAs: string,
  groupByParam: GeographyGroupBy,
) {
  const { filters } = usePerformanceReportContext();
  const allTimeStartDate = useAllTimeStartDate();
  const { data, isLoading, error } = usePayment('/reporting/summary', {
    params: {
      query: {
        ...buildReportingSummaryPerformanceQuery(
          filters,
          viewReportAs,
          allTimeStartDate,
        ),
        offset: 0,
        limit: 1000,
        transactionType: [
          PerformanceReportFilterTransactionType.listing,
          PerformanceReportFilterTransactionType.offer,
        ],
        groupBy: [groupByParam],
      },
    },
  });

  return useMemo(() => {
    const results = data?.results ?? [];
    const totalSalesVolume = results.reduce(
      (sum, row) => sum + (row.salesVolume ?? 0),
      0,
    );
    const rows = results.map((row, index) =>
      mapApiRowToGeographyPerformanceRow({
        row,
        index,
        totalSalesVolume,
        groupByValue: groupByParam,
      }),
    );

    return {
      rows,
      isLoading,
      error,
    };
  }, [data?.results, isLoading, error, groupByParam]);
}
